// path: dashboard/src/pages/programManagerDashboard/HOProjects.jsx

import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { FiSearch, FiDownload, FiLayout, FiFilter, FiSettings} from "react-icons/fi";
import HOSidebar from "../../components/MasterSideBar";
import TopNav from "../../components/TopbarNav";
import { fetchProjects } from "../../api/projectApi";
import { getCompliancePolicies, createCompliancePolicy, updateCompliancePolicy, deleteCompliancePolicy } from "../../api/projectApi";
import { useComplianceSummary } from "../../hooks/useCompliance";
import ProjectDashboardModal from "../Head-Of-Operations/ProjectDashboardModal";
import { FaRegFileAlt, FaExclamationTriangle, FaCheckCircle, FaBuilding, FaTimes, FaEdit, FaTrash } from "react-icons/fa";

export default function ProjectCompliance() {
    const [projects, setProjects] = useState([]);
    const [compliancePolicies, setCompliancePolicies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [complianceFilter, setComplianceFilter] = useState("all");
    const [ngoFilter, setNgoFilter] = useState("all");
    const projectsPerPage = 10;
    const navigate = useNavigate();
    const [isDashboardModalOpen, setIsDashboardModalOpen] = useState(false);
    const [selectedProjectId, setSelectedProjectId] = useState(null);
    const [sidebarVisible, setSidebarVisible] = useState(false);
    const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
    const exportDropdownRef = useRef(null);
    
    // Get real compliance summary data
    const { summary, loading: summaryLoading, refetch: refetchSummary } = useComplianceSummary();
    
    // Policy management states
    const [showPolicyModal, setShowPolicyModal] = useState(false);
    const [policyModalMode, setPolicyModalMode] = useState('view'); // 'view', 'create', 'edit'
    const [selectedPolicy, setSelectedPolicy] = useState(null);
    const [policyFilter, setPolicyFilter] = useState('all');
    const [policyForm, setPolicyForm] = useState({
        title: '',
        description: '',
        category: 'Legal',
        applicableTo: {
            projectTypes: [],
            locations: [],
            donors: [],
            minimumBudget: 0
        },
        isActive: true
    });

    // Function to fetch projects from the real API
    const fetchAllProjects = async () => {
        try {
            setLoading(true);
            const data = await fetchProjects(); // Call the API function
            
            // Add compliance-related fields to projects
            const projectsWithCompliance = data.map(project => {
                // Find compliance data for this project from the summary
                const projectSummary = summary?.find(s => s._id === project._id);
                
                return {
                    ...project,
                    compliance: projectSummary?.compliance || 0, // Use real compliance data
                    complianceTrend: projectSummary?.complianceTrend || 0, // Use real trend data
                    isNGO: Math.random() > 0.5, // Randomly assign for demo (remove this in production)
                    budgetStatus: 'Pending', // Default status
                    procurementStatus: 'Pending' // Default status
                };
            });
            
            setProjects(projectsWithCompliance);
            toast.success("Projects loaded successfully!");
        } catch (error) {
            console.error('Error fetching projects:', error);
            toast.error("Failed to fetch projects.");
            setProjects([]); // Set to empty array on failure
        } finally {
            setLoading(false);
        }
    };

    // Function to update projects with compliance data
    const updateProjectsWithComplianceData = () => {
        setProjects(prevProjects => {
            return prevProjects.map(project => {
                // Fixed: Use s._id instead of s.projectId
                const projectSummary = summary?.find(s => s._id === project._id);
                
                return {
                    ...project,
                    compliance: projectSummary?.compliance || project.compliance || 0,
                    complianceTrend: projectSummary?.complianceTrend || project.complianceTrend || 0
                };
            });
        });
    };

    // Function to fetch compliance policies
    const fetchAllPolicies = async () => {
        try {
            const policies = await getCompliancePolicies();
            setCompliancePolicies(policies || []);
        } catch (error) {
            console.error('Error fetching compliance policies:', error);
            // Don't show error toast for policies as it's not critical for basic functionality
            setCompliancePolicies([]);
        }
    };

    // Load projects immediately on component mount
    useEffect(() => {
        fetchAllProjects();
        fetchAllPolicies();
    }, []); // Empty dependency array to run only once on mount

    // Update projects with compliance data when summary is loaded
    useEffect(() => {
        if (summary && summary.length > 0 && projects.length > 0) {
            updateProjectsWithComplianceData();
        }
    }, [summary]); // Only run when summary changes

    // Refetch summary when compliance data might change
    useEffect(() => {
        const handleComplianceUpdate = () => {
            refetchSummary();
        };
        
        window.addEventListener('complianceUpdated', handleComplianceUpdate);
        return () => {
            window.removeEventListener('complianceUpdated', handleComplianceUpdate);
        };
    }, [refetchSummary]);

    const filteredProjects = useMemo(() => {
        let filtered = projects.filter((project) =>
            project.projectTitle && project.projectTitle.toLowerCase().includes(searchTerm.toLowerCase())
        );
        
        // Apply compliance filter
        if (complianceFilter !== "all") {
            filtered = filtered.filter(project => {
                if (complianceFilter === "high") return project.compliance > 80;
                if (complianceFilter === "medium") return project.compliance > 60 && project.compliance <= 80;
                if (complianceFilter === "low") return project.compliance <= 60;
                return true;
            });
        }
        
        // Apply NGO filter
        if (ngoFilter !== "all") {
            filtered = filtered.filter(project => {
                if (ngoFilter === "ngo") return project.isNGO;
                if (ngoFilter === "non-ngo") return !project.isNGO;
                return true;
            });
        }
        
        return filtered;
    }, [projects, searchTerm, complianceFilter, ngoFilter]);

    // Filter policies
    const filteredPolicies = useMemo(() => {
        if (policyFilter === 'all') return compliancePolicies;
        return compliancePolicies.filter(policy => policy.isActive);
    }, [compliancePolicies, policyFilter]);

    const indexOfLast = currentPage * projectsPerPage;
    const indexOfFirst = indexOfLast - projectsPerPage;
    const currentProjects = filteredProjects.slice(indexOfFirst, indexOfLast);
    const totalPages = Math.ceil(filteredProjects.length / projectsPerPage);

    const handleOpenDashboard = (projectId) => {
        setSelectedProjectId(projectId);
        setIsDashboardModalOpen(true);
    };

    const handleCloseDashboard = () => {
        setIsDashboardModalOpen(false);
        setSelectedProjectId(null);
    };
    
    const mapProjectToExportRow = (project) => ({
        "Project Title": project.projectTitle || "",
        "Location": project.location || "",
        "Donor": project.donor?.name || "N/A",
        "Start Date": project.startDate ? new Date(project.startDate).toLocaleDateString() : "",
        "End Date": project.endDate ? new Date(project.endDate).toLocaleDateString() : "",
        "Compliance %": project.compliance || 0,
        "Budget Status": project.budgetStatus || "Not Reviewed",
        "Procurement Status": project.procurementStatus || "Not Reviewed",
        "NGO Project": project.isNGO ? "Yes" : "No"
    });

    const handleExport = (format) => {
        setExportDropdownOpen(false);
        if (!filteredProjects.length) {
            toast.info("No projects to export.");
            return;
        }
        const exportData = filteredProjects.map(mapProjectToExportRow);
        
        switch (format) {
            case "excel":
                const worksheet = XLSX.utils.json_to_sheet(exportData);
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, "Projects Data");
                XLSX.writeFile(workbook, "projects_data.xlsx");
                break;
            case "csv":
                const csvContent = XLSX.utils.sheet_to_csv(XLSX.utils.json_to_sheet(exportData));
                const csvBlob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
                saveAs(csvBlob, "projects_data.csv");
                break;
            case "pdf":
                const doc = new jsPDF("l", "pt", "a4");
                autoTable(doc, {
                    head: [Object.keys(exportData[0])],
                    body: exportData.map(row => Object.values(row)),
                    startY: 60,
                });
                doc.save("projects_data.pdf");
                break;
            default:
                toast.error("Unsupported export format");
        }
        toast.success("Projects exported successfully!");
    };

    // Calculate compliance metrics from real data
    const calculateComplianceMetrics = () => {
        if (!summary || summary.length === 0) {
            return {
                overallCompliance: 0,
                pendingApprovals: 0,
                overdueItems: 0,
                completedRequirements: 0,
                totalRequirements: 0
            };
        }
        
        const totalRequirements = summary.reduce((sum, project) => sum + (project.totalItems || 0), 0);
        const completedRequirements = summary.reduce((sum, project) => sum + (project.completedItems || 0), 0);
        
        const overallCompliance = totalRequirements > 0 
            ? Math.round((completedRequirements / totalRequirements) * 100) 
            : 0;
            
        // These would need to be calculated from actual approval data
        const pendingApprovals = summary.reduce((sum, project) => {
            return sum + (project.pendingApprovals || 0);
        }, 0);
        
        const overdueItems = summary.reduce((sum, project) => {
            return sum + (project.overdueItems || 0);
        }, 0);
            
        return {
            overallCompliance,
            pendingApprovals,
            overdueItems,
            completedRequirements,
            totalRequirements
        };
    };
    
    const complianceMetrics = calculateComplianceMetrics();

    // Policy management functions
    const handleCreatePolicy = async () => {
        try {
            await createCompliancePolicy(policyForm);
            toast.success('Policy created successfully');
            setShowPolicyModal(false);
            setPolicyForm({
                title: '',
                description: '',
                category: 'Legal',
                applicableTo: {
                    projectTypes: [],
                    locations: [],
                    donors: [],
                    minimumBudget: 0
                },
                isActive: true
            });
            fetchAllPolicies();
        } catch (error) {
            toast.error('Failed to create policy');
        }
    };

    const handleUpdatePolicy = async () => {
        try {
            await updateCompliancePolicy(selectedPolicy._id, policyForm);
            toast.success('Policy updated successfully');
            setShowPolicyModal(false);
            setSelectedPolicy(null);
            fetchAllPolicies();
        } catch (error) {
            toast.error('Failed to update policy');
        }
    };

    const handleDeletePolicy = async (policyId) => {
        if (window.confirm('Are you sure you want to delete this policy?')) {
            try {
                await deleteCompliancePolicy(policyId);
                toast.success('Policy deleted successfully');
                fetchAllPolicies();
            } catch (error) {
                toast.error('Failed to delete policy');
            }
        }
    };

    const openPolicyModal = (mode, policy = null) => {
        setPolicyModalMode(mode);
        if (mode === 'create') {
            setPolicyForm({
                title: '',
                description: '',
                category: 'Legal',
                applicableTo: {
                    projectTypes: [],
                    locations: [],
                    donors: [],
                    minimumBudget: 0
                },
                isActive: true
            });
        } else if (mode === 'edit' && policy) {
            setPolicyForm({
                title: policy.title,
                description: policy.description,
                category: policy.category,
                applicableTo: policy.applicableTo,
                isActive: policy.isActive
            });
            setSelectedPolicy(policy);
        }
        setShowPolicyModal(true);
    };

    const closePolicyModal = () => {
        setShowPolicyModal(false);
        setSelectedPolicy(null);
        setPolicyModalMode('view');
    };

    return (
        <div className="flex">
            <HOSidebar visible={sidebarVisible} onClose={() => setSidebarVisible(false)} />
            <div className="flex-1 flex flex-col" style={{ marginLeft: '16rem' }}>
                <TopNav onSidebarToggle={() => setSidebarVisible(!sidebarVisible)} />
                <div className="p-6 bg-gray-50 flex-1 overflow-y-auto" style={{ marginTop: '4rem', height: 'calc(100vh - 4rem)' }}>
                    <div className="flex justify-between items-center mb-6">
                        <div>
                            <h1 className="text-2xl font-bold text-orange-600">Project Compliance Dashboard</h1>
                            <div className="text-sm text-gray-600 mt-3">
                                Review project plans, budgets, and compliance with organizational standards
                            </div>
                        </div>
                        <button
                            onClick={() => openPolicyModal('create')}
                            className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                        >
                            <FiSettings className="mr-2" />
                            Manage Policies
                        </button>
                    </div>
                    
                    {/* Compliance Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <div className="bg-white p-4 rounded-lg shadow">
                            <div className="flex items-center mb-2">
                                <FaCheckCircle className="text-green-500 mr-2" size={20} />
                                <h3 className="font-semibold text-gray-700">Overall Compliance</h3>
                            </div>
                            <div className="text-3xl font-bold text-orange-600">{complianceMetrics.overallCompliance}%</div>
                            <div className="text-sm text-gray-500">
                                {complianceMetrics.completedRequirements} of {complianceMetrics.totalRequirements} requirements met
                            </div>
                        </div>
                        
                        <div className="bg-white p-4 rounded-lg shadow">
                            <div className="flex items-center mb-2">
                                <FaRegFileAlt className="text-yellow-500 mr-2" size={20} />
                                <h3 className="font-semibold text-gray-700">Pending Approvals</h3>
                            </div>
                            <div className="text-3xl font-bold text-yellow-600">{complianceMetrics.pendingApprovals}</div>
                            <div className="text-sm text-gray-500">Awaiting Head of Operations review</div>
                        </div>
                        
                        <div className="bg-white p-4 rounded-lg shadow">
                            <div className="flex items-center mb-2">
                                <FaExclamationTriangle className="text-red-500 mr-2" size={20} />
                                <h3 className="font-semibold text-gray-700">Overdue Items</h3>
                            </div>
                            <div className="text-3xl font-bold text-red-600">{complianceMetrics.overdueItems}</div>
                            <div className="text-sm text-gray-500">Requires immediate attention</div>
                        </div>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
                        <div className="w-full sm:max-w-xs relative">
                            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
                            <input
                                type="text"
                                placeholder="Search projects by title..."
                                className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-500"
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setCurrentPage(1);
                                }}
                            />
                        </div>
                        
                        <div className="relative inline-block text-left" ref={exportDropdownRef}>
                            <button
                                onClick={() => setExportDropdownOpen(open => !open)}
                                type="button"
                                className="inline-flex justify-center items-center w-full sm:w-auto rounded-lg bg-orange-600 px-4 py-2 text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors"
                            >
                                <FiDownload className="mr-2" />
                                <span>Export</span>
                            </button>
                            {exportDropdownOpen && (
                                <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                                    <div className="py-1">
                                        {["excel", "csv", "pdf"].map((format) => (
                                            <button
                                                key={format}
                                                onClick={() => handleExport(format)}
                                                className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-orange-100 hover:text-orange-900"
                                            >
                                                Export as {format.toUpperCase()}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Filters */}
                    <div className="flex flex-wrap gap-2 mb-6">
                        <div className="flex items-center mr-4">
                            <span className="text-sm font-medium text-gray-700 mr-2">Compliance:</span>
                            <div className="flex gap-1">
                                <button
                                    onClick={() => setComplianceFilter('all')}
                                    className={`px-3 py-1 rounded text-sm ${complianceFilter === 'all' ? 'bg-orange-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                                >
                                    All
                                </button>
                                <button
                                    onClick={() => setComplianceFilter('high')}
                                    className={`px-3 py-1 rounded text-sm ${complianceFilter === 'high' ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                                >
                                    High
                                </button>
                                <button
                                    onClick={() => setComplianceFilter('medium')}
                                    className={`px-3 py-1 rounded text-sm ${complianceFilter === 'medium' ? 'bg-yellow-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                                >
                                    Medium
                                </button>
                                <button
                                    onClick={() => setComplianceFilter('low')}
                                    className={`px-3 py-1 rounded text-sm ${complianceFilter === 'low' ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                                >
                                    Low
                                </button>
                            </div>
                        </div>
                        
                        <div className="flex items-center">
                            <span className="text-sm font-medium text-gray-700 mr-2">Project Type:</span>
                            <div className="flex gap-1">
                                <button
                                    onClick={() => setNgoFilter('all')}
                                    className={`px-3 py-1 rounded text-sm ${ngoFilter === 'all' ? 'bg-orange-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                                >
                                    All
                                </button>
                                <button
                                    onClick={() => setNgoFilter('ngo')}
                                    className={`px-3 py-1 rounded text-sm ${ngoFilter === 'ngo' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                                >
                                    NGO
                                </button>
                                <button
                                    onClick={() => setNgoFilter('non-ngo')}
                                    className={`px-3 py-1 rounded text-sm ${ngoFilter === 'non-ngo' ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                                >
                                    Non-NGO
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {loading ? (
                            <p className="text-center py-8 text-gray-500 col-span-full">Loading projects...</p>
                        ) : currentProjects.length === 0 ? (
                            <p className="text-center py-10 text-gray-500 col-span-full">
                                {projects.length === 0 ? "No projects found." : "No projects match your filters."}
                            </p>
                        ) : (
                            currentProjects.map((project) => (
                                <div
                                    key={project._id}
                                    className="bg-white p-6 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer relative"
                                    onClick={() => handleOpenDashboard(project._id)}
                                >
                                    {/* NGO Status Badge */}
                                    {project.isNGO && (
                                        <div className="absolute top-4 left-4">
                                            <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full flex items-center">
                                                <FaBuilding size={10} className="mr-1" /> NGO
                                            </span>
                                        </div>
                                    )}
                                    
                                    {/* Compliance Status Badge */}
                                    <div className="absolute top-4 right-4">
                                        <span className={`px-3 py-1 rounded-full text-white text-sm font-medium ${
                                            project.compliance > 80 ? 'bg-green-500' : 
                                            project.compliance > 60 ? 'bg-yellow-500' : 'bg-red-500'
                                        }`}>
                                            {project.compliance > 80 ? 'High' : 
                                             project.compliance > 60 ? 'Medium' : 'Low'}
                                        </span>
                                    </div>
                                    
                                    <div className="flex items-center justify-between mb-4 mt-8">
                                        <h2 className="text-xl font-bold text-orange-700 hover:underline">{project.projectTitle}</h2>
                                        <div className="relative w-16 h-16">
                                            {/* Compliance Indicator */}
                                            <div className="w-full h-full rounded-full border-2 border-gray-200"></div>
                                            <div
                                                className={`absolute inset-0 rounded-full border-2 transform rotate-180 transition-all duration-500`}
                                                style={{
                                                    borderColor: project.compliance > 80 ? 'rgb(34, 197, 94)' : 
                                                                 project.compliance > 60 ? 'rgb(249, 115, 22)' : 'rgb(239, 68, 68)',
                                                    borderTopColor: 'transparent',
                                                    borderLeftColor: 'transparent',
                                                    clipPath: `inset(0 0 0 0 ${100 - (project.compliance || 0)}%)`,
                                                }}
                                            ></div>
                                            <span className="absolute inset-0 flex items-center justify-center font-bold text-sm text-gray-700">
                                                {project.compliance || 0}%
                                            </span>
                                        </div>
                                    </div>
                                    <div className="text-sm text-gray-600 space-y-1">
                                        <p><strong>Donor:</strong> {project.donor?.name || "N/A"}</p>
                                        <p><strong>Location:</strong> {project.location}</p>
                                        <p><strong>Start Date:</strong> {project.startDate ? new Date(project.startDate).toLocaleDateString() : "N/A"}</p>
                                        <div className="flex justify-between mt-2">
                                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                project.budgetStatus === 'Approved' ? 'bg-green-100 text-green-800' : 
                                                project.budgetStatus === 'Rejected' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                                            }`}>
                                                Budget: {project.budgetStatus || 'Pending'}
                                            </span>
                                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                project.procurementStatus === 'Approved' ? 'bg-green-100 text-green-800' : 
                                                project.procurementStatus === 'Rejected' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                                            }`}>
                                                Procurement: {project.procurementStatus || 'Pending'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex justify-end mt-4">
                                        <button
                                            className="flex items-center px-4 py-2 rounded-full border border-gray-300 text-gray-700 font-semibold text-sm hover:bg-orange-400 hover:text-white transition-all"
                                        >
                                            <FiLayout className="mr-2" /> Review Details
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    <div className="flex justify-center mt-6">
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                className={`rounded px-4 py-2 font-semibold transition-colors ${currentPage === 1 ? "cursor-not-allowed bg-gray-300 text-gray-500" : "bg-orange-100 text-orange-600 hover:bg-orange-200"}`}
                            >
                                Previous
                            </button>
                            {[...Array(totalPages)].map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setCurrentPage(i + 1)}
                                    className={`rounded px-4 py-2 font-semibold transition-colors ${currentPage === i + 1 ? "bg-orange-600 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"}`}
                                >
                                    {i + 1}
                                </button>
                            ))}
                            <button
                                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                className={`rounded px-4 py-2 font-semibold transition-colors ${currentPage === totalPages ? "cursor-not-allowed bg-gray-300 text-gray-500" : "bg-orange-100 text-orange-600 hover:bg-orange-200"}`}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            {/* The modal component to be displayed */}
            <ProjectDashboardModal
                isOpen={isDashboardModalOpen}
                onClose={handleCloseDashboard}
                projectId={selectedProjectId}
            />
            
            {/* Policy Management Modal */}
            {showPolicyModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">
                                {policyModalMode === 'create' ? 'Create Compliance Policy' : 
                                 policyModalMode === 'edit' ? 'Edit Compliance Policy' : 
                                 'Compliance Policies'}
                            </h2>
                            <button
                                onClick={closePolicyModal}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                < FaTimes size={24} />
                            </button>
                        </div>
                        
                        {policyModalMode === 'view' ? (
                            // View Policies Mode
                            <div>
                                <div className="flex justify-between items-center mb-4">
                                    <div className="flex items-center space-x-2">
                                        <span className="text-sm font-medium text-gray-700">Filter:</span>
                                        <select
                                            value={policyFilter}
                                            onChange={(e) => setPolicyFilter(e.target.value)}
                                            className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                                        >
                                            <option value="all">All Policies</option>
                                            <option value="active">Active Only</option>
                                        </select>
                                    </div>
                                    <button
                                        onClick={() => openPolicyModal('create')}
                                        className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center"
                                    >
                                        <FiSettings className="mr-2" />
                                        Create New Policy
                                    </button>
                                </div>
                                
                                {filteredPolicies.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500">
                                        No policies found
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {filteredPolicies.map(policy => (
                                            <div key={policy._id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                                                <div className="flex justify-between items-start">
                                                    <div className="flex-1">
                                                        <h3 className="text-lg font-medium text-gray-900">{policy.title}</h3>
                                                        <p className="text-gray-600 text-sm mt-1">{policy.description}</p>
                                                        
                                                        <div className="mt-2">
                                                            <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                                                                {policy.category}
                                                            </span>
                                                            <span className={`px-2 py-1 text-xs rounded-full ml-2 ${
                                                                policy.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                                            }`}>
                                                                {policy.isActive ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </div>
                                                        
                                                        {policy.applicableTo && (
                                                            <div className="mt-3 text-xs text-gray-500">
                                                                <div className="font-medium">Applies to:</div>
                                                                {policy.applicableTo.projectTypes?.length > 0 && (
                                                                    <div>Project Types: {policy.applicableTo.projectTypes.join(', ')}</div>
                                                                )}
                                                                {policy.applicableTo.locations?.length > 0 && (
                                                                    <div>Locations: {policy.applicableTo.locations.join(', ')}</div>
                                                                )}
                                                                {policy.applicableTo.donors?.length > 0 && (
                                                                    <div>Donors: {policy.applicableTo.donors.join(', ')}</div>
                                                                )}
                                                                {policy.applicableTo.minimumBudget > 0 && (
                                                                    <div>Min Budget: ${policy.applicableTo.minimumBudget.toLocaleString()}</div>
                                                                )}
                                                            </div>)}
                                                     
                                                    </div>
                                                    
                                                    <div className="flex space-x-2 ml-4">
                                                        <button
                                                            onClick={() => openPolicyModal('edit', policy)}
                                                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                            title="Edit Policy"
                                                        >
                                                            <FaEdit size={16} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeletePolicy(policy._id)}
                                                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                            title="Delete Policy"
                                                        >
                                                            <FaTrash size={16} />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            // Create/Edit Policy Form
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-4">
                                    {policyModalMode === 'create' ? 'Create New Policy' : 'Edit Policy'}
                                </h3>
                                
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                                        <input
                                            type="text"
                                            value={policyForm.title}
                                            onChange={(e) => setPolicyForm({...policyForm, title: e.target.value})}
                                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none"
                                            placeholder="Enter policy title"
                                        />
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                                        <select
                                            value={policyForm.category}
                                            onChange={(e) => setPolicyForm({...policyForm, category: e.target.value})}
                                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none"
                                        >
                                            <option value="Legal">Legal</option>
                                            <option value="Financial">Financial</option>
                                            <option value="Operational">Operational</option>
                                            <option value="HR">HR</option>
                                            <option value="Reporting">Reporting</option>
                                            <option value="Donor Compliance">Donor Compliance</option>
                                            <option value="Safeguarding">Safeguarding</option>
                                            <option value="Anti-Fraud">Anti-Fraud</option>
                                            <option value="Environmental">Environmental</option>
                                            <option value="Social">Social</option>
                                            <option value="M&E">M&E</option>
                                        </select>
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                        <textarea
                                            value={policyForm.description}
                                            onChange={(e) => setPolicyForm({...policyForm, description: e.target.value})}
                                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none"
                                            rows="3"
                                            placeholder="Enter policy description"
                                        />
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Applicability</label>
                                        <div className="bg-gray-50 p-3 rounded-lg">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-xs text-gray-600 mb-1">Project Types (comma separated)</label>
                                                    <input
                                                        type="text"
                                                        value={policyForm.applicableTo.projectTypes.join(', ')}
                                                        onChange={(e) => setPolicyForm({
                                                            ...policyForm, 
                                                            applicableTo: {
                                                                ...policyForm.applicableTo,
                                                                projectTypes: e.target.value.split(',').map(t => t.trim()).filter(t => t)
                                                            }
                                                        })}
                                                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:outline-none text-sm"
                                                        placeholder="e.g. Health, Education, Emergency"
                                                    />
                                                </div>
                                                
                                                <div>
                                                    <label className="block text-xs text-gray-600 mb-1">Locations (comma separated)</label>
                                                    <input
                                                        type="text"
                                                        value={policyForm.applicableTo.locations.join(', ')}
                                                        onChange={(e) => setPolicyForm({
                                                            ...policyForm, 
                                                            applicableTo: {
                                                                ...policyForm.applicableTo,
                                                                locations: e.target.value.split(',').map(t => t.trim()).filter(t => t)
                                                            }
                                                        })}
                                                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:outline-none text-sm"
                                                        placeholder="e.g. Juba, Bor, Malakal"
                                                    />
                                                </div>
                                                
                                                <div>
                                                    <label className="block text-xs text-gray-600 mb-1">Donors (comma separated)</label>
                                                    <input
                                                        type="text"
                                                        value={policyForm.applicableTo.donors.join(', ')}
                                                        onChange={(e) => setPolicyForm({
                                                            ...policyForm, 
                                                            applicableTo: {
                                                                ...policyForm.applicableTo,
                                                                donors: e.target.value.split(',').map(t => t.trim()).filter(t => t)
                                                            }
                                                        })}
                                                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:outline-none text-sm"
                                                        placeholder="e.g. USAID, UNICEF, EU"
                                                    />
                                                </div>
                                                
                                                <div>
                                                    <label className="block text-xs text-gray-600 mb-1">Minimum Budget ($)</label>
                                                    <input
                                                        type="number"
                                                        value={policyForm.applicableTo.minimumBudget}
                                                        onChange={(e) => setPolicyForm({
                                                            ...policyForm, 
                                                            applicableTo: {
                                                                ...policyForm.applicableTo,
                                                                minimumBudget: Number(e.target.value)
                                                            }
                                                        })}
                                                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:outline-none text-sm"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={policyForm.isActive}
                                            onChange={(e) => setPolicyForm({...policyForm, isActive: e.target.checked})}
                                            className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                                        />
                                        <label className="ml-2 text-sm text-gray-700">Active Policy</label>
                                    </div>
                                </div>
                                
                                <div className="flex justify-end mt-6 space-x-2">
                                    <button
                                        onClick={closePolicyModal}
                                        className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={policyModalMode === 'create' ? handleCreatePolicy : handleUpdatePolicy}
                                        className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                                    >
                                        {policyModalMode === 'create' ? 'Create Policy' : 'Save Changes'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}